import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  createUserWithEmailAndPassword, 
  updateProfile,
  signInWithEmailAndPassword,
  GoogleAuthProvider, 
  GithubAuthProvider, 
  signInWithPopup 
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { motion, AnimatePresence } from 'framer-motion';

const AuthPage: React.FC = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);

  // Handle browser back button
  useEffect(() => {
    const handlePopState = () => {
      // Navigate to landing page when browser back button is pressed
      navigate('/');
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [navigate]);

  const handleGoogleSignIn = async () => {
    try {
      setIsLoading(true);
      setError('');
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGithubSignIn = async () => {
    try {
      setIsLoading(true);
      setError('');
      const provider = new GithubAuthProvider();
      await signInWithPopup(auth, provider);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const { email, password, name } = e.currentTarget.elements as any;
    setIsLoading(true);
    setError('');
    if (isLogin) {
      try {
        await signInWithEmailAndPassword(auth, email.value, password.value);
        navigate('/dashboard');
      } catch (err: any) {
        setError('Invalid email or password. Please try again.');
      } finally {
        setIsLoading(false);
      }
    } else {
      try {
        const userCredential = await createUserWithEmailAndPassword(auth, email.value, password.value);
        if (userCredential.user) {
          await updateProfile(userCredential.user, { displayName: name.value });
        }
        navigate('/dashboard');
      } catch (err: any) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col md:flex-row bg-gradient-to-br from-[#0f172a] to-[#1e293b]">
      {/* Back button */}
      <button
        onClick={() => navigate('/')}
        className="absolute top-4 left-4 z-10 flex items-center space-x-2 text-gray-300 hover:text-white transition-colors"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5M12 19l-7-7 7-7"/>
        </svg>
        <span className="text-sm font-medium">Back to Home</span>
      </button>
      
      {/* Top: Logo and tagline for mobile */}
      <div className="md:hidden flex flex-col justify-center items-center py-8 px-4">
        <div className="text-4xl sm:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500 mb-4 text-center">
          StyleSync
        </div>
        <div className="text-lg sm:text-xl text-gray-300 font-light text-center">
          Where teams create visually
        </div>
      </div>

      {/* Left: Logo and tagline for desktop */}
      <div className="hidden md:flex flex-col justify-center items-center w-1/2">
        <div className="text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500 mb-8">
          StyleSync
        </div>
        <div className="text-2xl text-gray-300 font-light text-center">
          Where teams create visually
        </div>
      </div>

      {/* Right: Auth card */}
      <div className="flex-1 flex justify-center items-center px-4 py-8 md:py-0">
        <div className="bg-[#18181b] rounded-2xl shadow-2xl p-6 sm:p-8 md:p-12 w-full max-w-md min-h-[480px]">
          <AnimatePresence mode="wait">
            {isLogin ? (
              <motion.div
                key="login"
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.3 }}
              >
                <h2 className="text-xl sm:text-2xl font-bold text-white mb-6 text-center">Login</h2>
                <div className="max-w-sm mx-auto w-full">
                  <form className="space-y-4" onSubmit={handleSubmit}>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2">
                        {/* Email icon */}
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 48 48" className="text-blue-400">
                          <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" d="M38.735 15.224L24 26.072L9.265 15.224" strokeWidth="1"/>
                          <rect width="39" height="26.582" x="4.5" y="10.709" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" rx="4" ry="4" strokeWidth="1"/>
                        </svg>
                      </span>
                      <input
                        type="email"
                        className="w-full pl-10 pr-3 py-3 sm:py-2 rounded-md bg-[#23232b] text-white text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="designer@email.com"
                        id="email"
                        required
                        onChange={() => setError('')}
                      />
                    </div>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2">
                        {/* Password icon */}
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                          <g fill="none">
                            <path fill="url(#tokenBrandedKey0)" d="M11.5 16.5a1 1 0 1 0 0-2a1 1 0 0 0 0 2"/>
                            <path fill="url(#tokenBrandedKey1)" fillRule="evenodd" d="M12 3L4 7.485v8.99L12 21l8-4.525v-8.99zm2.045 3.5H11.54c-.19.005-.53.14-.54.615v6.448a2 2 0 1 0 1 0V9.5h1.58c.15 0 .42-.17.42-.5c0-.325-.265-.5-.42-.5H12v-1h2.05c.175 0 .45-.195.45-.5a.48.48 0 0 0-.455-.5" clipRule="evenodd"/>
                            <defs>
                              <linearGradient id="tokenBrandedKey0" x1="12.001" x2="12.001" y1="3" y2="21" gradientUnits="userSpaceOnUse">
                                <stop stopColor="#4ab0e1"/>
                                <stop offset="1" stopColor="#58b9cf"/>
                              </linearGradient>
                              <linearGradient id="tokenBrandedKey1" x1="12" x2="12" y1="3" y2="21" gradientUnits="userSpaceOnUse">
                                <stop stopColor="#4ab0e1"/>
                                <stop offset="1" stopColor="#58b9cf"/>
                              </linearGradient>
                            </defs>
                          </g>
                        </svg>
                      </span>
                      <input
                        type="password"
                        className="w-full pl-10 pr-8 py-3 sm:py-2 rounded-md bg-[#23232b] text-white text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Password"
                        id="password"
                        required
                        onChange={() => setError('')}
                      />
                    </div>
                    <button
                      type="submit"
                      className={`w-full py-3 sm:py-2 rounded-md bg-gradient-to-r from-blue-500 to-purple-500 text-white font-semibold text-base shadow-md transition ${
                        isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:from-blue-600 hover:to-purple-600'
                      }`}
                      disabled={isLoading}
                    >
                      {isLoading ? (isLogin ? 'Signing in...' : 'Creating account...') : (isLogin ? 'Login' : 'Sign up')}
                    </button>
                  </form>
                  {error && (
                    <div className="mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative text-center text-sm" role="alert">
                      <span className="block sm:inline">{error}</span>
                    </div>
                  )}
                  <div className="my-6 flex items-center justify-center text-gray-400">
                    <span className="border-t border-gray-600 flex-1 mr-4"></span>
                    <span className="text-sm">or continue with</span>
                    <span className="border-t border-gray-600 flex-1 ml-4"></span>
                  </div>
                  <div className="flex items-center justify-center space-x-4">
                    <button
                      onClick={handleGoogleSignIn}
                      className="flex items-center justify-center w-12 h-12 rounded-full bg-white hover:bg-gray-100 shadow-md transition-colors"
                    >
                      {/* Google icon */}
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 48 48">
                        <path fill="#ffc107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917"/>
                        <path fill="#ff3d00" d="m6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691"/>
                        <path fill="#4caf50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.9 11.9 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44"/>
                        <path fill="#1976d2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917"/>
                      </svg>
                    </button>
                    <span className="text-gray-400 text-sm">or</span>
                    <button
                      onClick={handleGithubSignIn}
                      className="flex items-center justify-center w-12 h-12 rounded-full bg-white hover:bg-gray-100 shadow-md transition-colors"
                    >
                      {/* GitHub icon */}
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                        <path fill="currentColor" d="M12 2A10 10 0 0 0 2 12c0 4.42 2.87 8.17 6.84 9.5c.5.08.66-.23.66-.5v-1.69c-2.77.6-3.36-1.34-3.36-1.34c-.46-1.16-1.11-1.47-1.11-1.47c-.91-.62.07-.6.07-.6c1 .07 1.53 1.03 1.53 1.03c.87 1.52 2.34 1.07 2.91.83c.09-.65.35-1.09.63-1.34c-2.22-.25-4.55-1.11-4.55-4.92c0-1.11.38-2 1.03-2.71c-.1-.25-.45-1.29.1-2.64c0 0 .84-.27 2.75 1.02c.79-.22 1.65-.33 2.5-.33s1.71.11 2.5.33c1.91-1.29 2.75-1.02 2.75-1.02c.55 1.35.2 2.39.1 2.64c.65.71 1.03 1.6 1.03 2.71c0 3.82-2.34 4.66-4.57 4.91c.36.31.69.92.69 1.85V21c0 .27.16.59.67.5C19.14 20.16 22 16.42 22 12A10 10 0 0 0 12 2"/>
                      </svg>
                    </button>
                  </div>
                  <div className="mt-8 text-center text-gray-400 text-xs sm:text-sm">
                    Don't have an account?{' '}
                    <button type="button" className="text-purple-400 cursor-pointer hover:text-purple-300 transition-colors" onClick={() => setIsLogin(false)}>
                      Sign up
                    </button>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="signup"
                initial={{ opacity: 0, x: -40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 40 }}
                transition={{ duration: 0.3 }}
              >
                <h2 className="text-xl sm:text-2xl font-bold text-white mb-6 text-center">Sign up</h2>
                <div className="max-w-sm mx-auto w-full">
                  <form className="space-y-4" onSubmit={handleSubmit}>
                    <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-2">
                      <input
                        type="text"
                        className="w-full sm:w-1/2 px-3 py-3 sm:py-2 rounded-md bg-[#23232b] text-white text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="First Name"
                        id="name"
                        required
                      />
                      <input
                        type="text"
                        className="w-full sm:w-1/2 px-3 py-3 sm:py-2 rounded-md bg-[#23232b] text-white text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Last Name"
                        id="name"
                        required
                      />
                    </div>
                    <input
                      type="email"
                      className="w-full px-3 py-3 sm:py-2 rounded-md bg-[#23232b] text-white text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Email"
                      id="email"
                      required
                    />
                    <input
                      type="password"
                      className="w-full px-3 py-3 sm:py-2 rounded-md bg-[#23232b] text-white text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Password"
                      id="password"
                      required
                    />
                    <button
                      type="submit"
                      className={`w-full py-3 sm:py-2 rounded-md bg-gradient-to-r from-blue-500 to-purple-500 text-white font-semibold text-base shadow-md transition ${
                        isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:from-blue-600 hover:to-purple-600'
                      }`}
                      disabled={isLoading}
                    >
                      {isLoading ? (isLogin ? 'Signing in...' : 'Creating account...') : (isLogin ? 'Login' : 'Sign up')}
                    </button>
                  </form>
                  {error && (
                    <div className="mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative text-center text-sm" role="alert">
                      <span className="block sm:inline">{error}</span>
                    </div>
                  )}
                  <div className="my-6 flex items-center justify-center text-gray-400">
                    <span className="border-t border-gray-600 flex-1 mr-4"></span>
                    <span className="text-sm">or continue with</span>
                    <span className="border-t border-gray-600 flex-1 ml-4"></span>
                  </div>
                  <div className="flex items-center justify-center space-x-4">
                    <button
                      onClick={handleGoogleSignIn}
                      className="flex items-center justify-center w-12 h-12 rounded-full bg-white hover:bg-gray-100 shadow-md transition-colors"
                    >
                      {/* Google icon */}
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 48 48">
                        <path fill="#ffc107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917"/>
                        <path fill="#ff3d00" d="m6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691"/>
                        <path fill="#4caf50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.9 11.9 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44"/>
                        <path fill="#1976d2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917"/>
                      </svg>
                    </button>
                    <span className="text-gray-400 text-sm">or</span>
                    <button
                      onClick={handleGithubSignIn}
                      className="flex items-center justify-center w-12 h-12 rounded-full bg-white hover:bg-gray-100 shadow-md transition-colors"
                    >
                      {/* GitHub icon */}
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                        <path fill="currentColor" d="M12 2A10 10 0 0 0 2 12c0 4.42 2.87 8.17 6.84 9.5c.5.08.66-.23.66-.5v-1.69c-2.77.6-3.36-1.34-3.36-1.34c-.46-1.16-1.11-1.47-1.11-1.47c-.91-.62.07-.6.07-.6c1 .07 1.53 1.03 1.53 1.03c.87 1.52 2.34 1.07 2.91.83c.09-.65.35-1.09.63-1.34c-2.22-.25-4.55-1.11-4.55-4.92c0-1.11.38-2 1.03-2.71c-.1-.25-.45-1.29.1-2.64c0 0 .84-.27 2.75 1.02c.79-.22 1.65-.33 2.5-.33s1.71.11 2.5.33c1.91-1.29 2.75-1.02 2.75-1.02c.55 1.35.2 2.39.1 2.64c.65.71 1.03 1.6 1.03 2.71c0 3.82-2.34 4.66-4.57 4.91c.36.31.69.92.69 1.85V21c0 .27.16.59.67.5C19.14 20.16 22 16.42 22 12A10 10 0 0 0 12 2"/>
                      </svg>
                    </button>
                  </div>
                  <div className="mt-8 text-center text-gray-400 text-xs sm:text-sm">
                    Already have an account?{' '}
                    <button type="button" className="text-purple-400 cursor-pointer hover:text-purple-300 transition-colors" onClick={() => setIsLogin(true)}>
                      Login
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default AuthPage; 